import axios from 'axios'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import moment from 'moment'
import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import Nav from '../components/Nav/Nav'
import { checkHasAccessModule, clearUserRoleCache } from '../helpers/checkHasAccess'
import SignUp from '../pages/SignUp'
import { auth, rolesCollection, tableViewsCollection } from '../services/firebase/firebaseConfig'
import styles from './mainRoutes.styles.module.css'

import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride'
import { GlobalContext } from '../core/context/globalContext'
import { fetchBillingData, fetchData, fetchDispatchData, setGuidesStepIndex, setTriggerSystemSetupModal } from './redux/store'
import {
    fetchUserData,
    initUserData,
    resetUserData,
    setUserDataLoaded,
    updateActions,
    updateGuideWalkthrough,
    updateRole,
} from './redux/store/userSlice/userSlice'

import AIAssistant from '../components/AI/AIAssistant'
import GuideWelcome from '../components/Guides/GuideWelcome'
import LoadingScreen from '../components/LoadingScreen/LoadingScreen'
import RouteErrorBoundary from './RouteErrorBoundary'
import { lazyWithRetry } from './lazyWithRetry'

console.log('[mainRoutes.tsx] File loaded')

const Admin = lazyWithRetry(() => import('../pages/Admin'))
const ExternalIntegrations = lazyWithRetry(() => import('../pages/Integrations/External'))
const InternalIntegrations = lazyWithRetry(() => import('../pages/Integrations/Internal'))
const QuickBooksIntegration = lazyWithRetry(() => import('../pages/Integrations/QuickBooks'))
const ShyftbaseAI = lazyWithRetry(() => import('../pages/AI/ShyftbaseAI'))
const Optimizer = lazyWithRetry(() => import('../pages/AI/Optimizer'))
const AgentConfiguration = lazyWithRetry(() => import('../pages/Admin/AI/AgentConfiguration'))
const Assets = lazyWithRetry(() => import('../pages/Assets'))
const AdminRoleSettings = lazyWithRetry(() => import('../pages/Admin/view/RoleSettings'))
const AdminUserRoleSettings = lazyWithRetry(() => import('../pages/Admin/view/UserRoleSettings'))
const AdminOrderTemplatesSettings = lazyWithRetry(() => import('../pages/Admin/view/OrderTemplatesSettings'))
const ChangePassword = lazyWithRetry(() => import('../pages/DateRegion/changePassword'))
const Conditions = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/Conditions/Conditions'))
const ContractorProfileSettings = lazyWithRetry(() => import('../pages/Admin/view/ContractorProfileSettings'))
const DateRegion = lazyWithRetry(() => import('../pages/DateRegion'))
const ForgotPassword = lazyWithRetry(() => import('../pages/ForgotPassword'))
const FSCRates = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/FSCRates/FSCRates'))
const Login = lazyWithRetry(() => import('../pages/Login'))
const PendingApproval = lazyWithRetry(() => import('../pages/PendingApproval'))
const NotificationSettings = lazyWithRetry(() => import('../pages/Admin/view/NotificationSettings'))
const MapFleet = lazyWithRetry(() => import('../pages/MapFleet'))
const OrderTable = lazyWithRetry(() => import('../pages/OrderTable'))
const RateTableSettings = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/RateTableSettings'))
const Reports = lazyWithRetry(() => import('../pages/Reports'))
const Search = lazyWithRetry(() => import('../pages/Search'))
const LiveConnect = lazyWithRetry(() => import('../pages/LiveConnect'))
const Services = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/Services/Services'))
const SubStatus = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/SubStatus/SubStatus'))
const TiersEscrow = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/TiersEscrow'))
const VenderProfileSettings = lazyWithRetry(() => import('../pages/Admin/view/VendorProfileSettings'))
const WeeklyFSCRates = lazyWithRetry(
    () => import('../pages/Admin/view/components/BillingAdmin/AdminTab/WeeklyFSCRates/WeeklyFSCRates')
)
const Taxes = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/Taxes/Taxes'))
const CalendarSettings = lazyWithRetry(() => import('../pages/Admin/view/CalendarSettings'))
const AutomationSettings = lazyWithRetry(() => import('../pages/Admin/view/AutomationSettings'))
const ServiceTypeSettings = lazyWithRetry(() => import('../pages/Admin/view/ServiceTypeSettings'))
const SharedView = lazyWithRetry(() => import('../pages/SharedView'))
const GlobalUnits = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/GlobalUnits/GlobalUnits'))
const EmailIntake = lazyWithRetry(() => import('../pages/EmailIntake'))

const FSC = lazyWithRetry(() => import('../pages/Admin/view/components/BillingAdmin/AdminTab/FSC/FSC'))

// Dashboard and KPIs Components
const Dashboard = lazyWithRetry(() => import('../pages/Dashboard'))
const KPIs = lazyWithRetry(() => import('../pages/KPIs'))

// Dispatch Dashboard + Single-date Route Optimizer
const Dispatch = lazyWithRetry(() => import('../pages/Dispatch'))

// Mid-Mile Admin Components
const MidMileAdmin = lazyWithRetry(() => import('../pages/Admin/MidMile'))
const RateTablesManagement = lazyWithRetry(() => import('../pages/Admin/MidMile/RateTables'))
const TariffBuilder = lazyWithRetry(() => import('../pages/Admin/MidMile/Tariffs'))
const BiddingConfiguration = lazyWithRetry(() => import('../pages/Admin/MidMile/Bidding'))
const MidMileSettings = lazyWithRetry(() => import('../pages/Admin/MidMile/Settings'))

// Mid-Mile Operational Components
const MidMileShipmentCreate = lazyWithRetry(() => import('../pages/MidMile/ShipmentCreate'))
const ActiveBidding = lazyWithRetry(() => import('../pages/Admin/MidMile/Bidding/Active'))

// Transport Partners Management Components
const PartnerDirectory = lazyWithRetry(() => import('../pages/Admin/TransportPartners/Directory/index'))
const PartnerProfiles = lazyWithRetry(() => import('../pages/Admin/TransportPartners/Profiles/index'))
const Drivers = lazyWithRetry(() => import('../pages/Admin/TransportPartners/Drivers/index'))
const AIRateIntake = lazyWithRetry(() => import('../pages/Admin/TransportPartners/RateIntake/index'))

// Billing & Settlement Components
const AuditAdjustments = lazyWithRetry(() => import('../pages/Admin/Billing/AuditAdjustments/index'))

// Address Book Component
const AddressBookSettings = lazyWithRetry(() => import('../pages/Admin/view/AddressBookSettings'))

// Field Management Component
const FieldManagement = lazyWithRetry(() => import('../pages/Admin/FieldManagement'))
const EmailTemplates = lazyWithRetry(() => import('../pages/Admin/EmailTemplates'))

// Operations & Routing Components (Phase 1 - Priority 1)
const CapacityManagement = lazyWithRetry(() => import('../pages/Admin/Operations/CapacityManagement'))
const ReOptimization = lazyWithRetry(() => import('../pages/Admin/Operations/ReOptimization'))

// Compliance Components (Phase 1 - Priority 1)
const GDPR = lazyWithRetry(() => import('../pages/Admin/Compliance/GDPR'))
const CountryRules = lazyWithRetry(() => import('../pages/Admin/Compliance/CountryRules'))
const AuditTrails = lazyWithRetry(() => import('../pages/Admin/Compliance/AuditTrails'))

// Rates Management Component
const RatesManagement = lazyWithRetry(() => import('../pages/Admin/Rates'))

// QuickBooks OAuth Callback
const QuickBooksOAuthCallback = lazyWithRetry(() => import('../pages/QuickBooksOAuthCallback'))

// Dispute Resolution — owner workspace, and the public customer review page
const Disputes = lazyWithRetry(() => import('../pages/Disputes'))
const DisputeReview = lazyWithRetry(() => import('../pages/DisputeReview'))

const MainRoutes = () => {
    const [run, setRun] = useState(false)
    const [steps, setSteps] = useState<any[]>([])
    const isAuthenticatingRef = useRef(false)
    const [hasAuthenticatedInSession, setHasAuthenticatedInSession] = useState(false)
    const shipmentsInitializedRef = useRef(false)
    const {
        user: { updateUser, user, displayName },
        table: { selectTableSelectedDate, addCountForFetch, setSelectedView },
        ui: { updateUIMode },
    } = useContext(GlobalContext)
    const dispatch = useDispatch()
    const allPreFetchDataLoaded = useSelector(
        (state: any) =>
            state.data.loaded && state.billingData.billingDataloaded && state.dispatchData.dispatchDataLoaded
    )
    const { role, userDataLoaded, completedGuidesWalkthrough, distributionzones: DISTRIBUTIONZONES, clients: VENDORS, isAllClients, isAllDistributionZones } = useSelector((state: any) => state.user)
    const { guidesStepIndex } = useSelector((state: any) => state.data)

    const location = useLocation()
    const history = useHistory()
    const [openSnackbar] = useSnackbar()
    const { t } = useTranslation()

    const { rateId, viewId, tokenId } = useParams<any>()

    const goToPage = useCallback(
        (path) => {
            history.push(path)
        },
        [history]
    )

    useHotkeys('ctrl+shift+1', () => goToPage('/dateRegion'))
    useHotkeys('ctrl+shift+2', () => goToPage('/routes'))
    useHotkeys('ctrl+shift+3', () => goToPage('/mapFleet/network'))
    useHotkeys('ctrl+shift+4', () => goToPage('/reports'))
    useHotkeys('ctrl+shift+6', () => goToPage('/admin'))

    useEffect(() => {
        console.log('Setting up Joyride steps...')
        const _steps: any[] = [
            {
                target: '.guide-start',
                content: <GuideWelcome />,
                route: '/dashboard',
                disableBeacon: true, // This causes it to start automatically.
            },
            {
                target: '.guide-dashboard',
                content: 'Welcome to your Dashboard! Get a quick overview of your operations, key metrics, and important updates at a glance.',
                route: '/dashboard',
            },
            {
                target: '.guide-dateRegion',
                content: 'This is where records live. Here you can manage and track all your orders & shipments.',
                route: '/dateRegion',
            },
            {
                target: '.guide-assets',
                content: 'The Assets module helps you manage your SKUs, and keep a catalog of all your products. Add warranties, installation instructions and more.',
                route: '/assets',
            },
            {
                target: '.guide-network',
                content: 'Use the Network module to manage your entire distribution network. Create your hubs, zones, stores and links between them. Manage your days of operation and more.',
                route: '/mapFleet',
            },
            {
                target: '.guide-analytics',
                content: 'Access detailed reports and analytics about your operations here.',
                route: '/analytics',
            },
            {
                target: '.guide-kpis',
                content: 'Monitor your Key Performance Indicators (KPIs) and track critical business metrics in real-time.',
                route: '/kpis',
            },
        ]
        console.log('Initial navigation steps added:', _steps)

        // Add dateRegion (records) steps BEFORE admin steps
        if (checkHasAccessModule('/dateRegion')) {
            console.log('Adding dateRegion steps...')
            _steps.push(
                {
                    target: '.guide-creationFlow',
                    content: 'Create orders manually or import them using csv / excel files.',
                    route: '/dateRegion',
                },
                {
                    target: '.guide-ViewToolPanel',
                    content: 'Utilize views for predefined filters and queries.',
                    route: '/dateRegion',
                },
            )
            console.log('Added dateRegion steps:', _steps.slice(-2))
        }

        // Add admin step after dateRegion
        if (checkHasAccessModule('/admin')) {
            console.log('Adding admin navigation step...')
            _steps.push({
                target: '.guide-admin',
                content: 'The Admin module is where you will configure all your business settings.',
                route: '/admin',
            })
        }

        // Add final system setup step
        if (checkHasAccessModule('/admin')) {
            console.log('Adding admin system setup step...')
            _steps.push({
                target: 'body',
                content: 'Now let\'s set up your system! We\'ll guide you through the essential configuration steps.',
                route: '/admin',
                placement: 'center',
                disableBeacon: true,
            })
            console.log('Added admin system setup step')
        }
        console.log('Final steps array:', _steps)
        setSteps(_steps)
    }, [])

    const handleJoyrideCallback = useCallback(
        async (data: CallBackProps) => {
            console.log('Joyride callback triggered with data:', data)
            const { action, index, status, type } = data
            const nextStep = steps[index + 1]
            const currentStep = steps[index]
            console.log('Current step index:', index)
            console.log('Next step:', nextStep)
            console.log('Total steps:', steps.length)

            // Check if user clicks NEXT on the final system setup step
            // This step has target 'body' and route '/admin'
            // Trigger the system setup modal to open
            if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT && currentStep?.target === 'body' && currentStep?.route === '/admin') {
                console.log('Triggering system setup modal from final Joyride step')
                dispatch(setTriggerSystemSetupModal(true))
            }

            // Your callback logic, updating currentStepIndex as needed
            if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
                console.log('Moving to next step:', index + 1)
                dispatch(setGuidesStepIndex(index + 1))
            } else if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
                console.log('Moving to previous step:', Math.max(index - 1, 0))
                dispatch(setGuidesStepIndex(Math.max(index - 1, 0)))
            }

            if (
                status === STATUS.FINISHED &&
                index === steps.length - 1 &&
                action !== ACTIONS.CLOSE &&
                action !== ACTIONS.UPDATE
            ) {
                console.log('Tour finished, updating guide walkthrough status')
                setRun(false) // End the tour if finished, skipped, or closed
                await dispatch(updateGuideWalkthrough(true))
            } else if (type === EVENTS.STEP_AFTER && nextStep && nextStep.route) {
                console.log('Navigating to next step route:', nextStep.route)
                // If there is a next step, and it has a route, navigate to it
                setTimeout(() => history.push(nextStep.route), 0) // Adding a slight delay
            } else if (action === ACTIONS.PREV && index > 0 && steps[index - 1].route) {
                console.log('Navigating to previous step route')
                // If the PREVIOUS step has a route, navigate to it
                history.goBack()
            }
        },
        [steps, history, dispatch, updateGuideWalkthrough]
    )

    useEffect(() => {
        let timeOut
        try {
            onAuthStateChanged(auth, async (user) => {
                try {
                    if (user) {
                        if (location.pathname === '/login' || location.pathname === '/') {
                            history.push('/shipments')
                            return
                        }

                        // Prevent concurrent authentication attempts using ref for immediate synchronous check
                        if (isAuthenticatingRef.current) {
                            console.log('Already authenticating, skipping...')
                            return
                        }

                        // If user is already fully loaded and authenticated, skip re-validation
                        // This prevents loops when Firebase updates custom claims
                        if (userDataLoaded && role && hasAuthenticatedInSession) {
                            console.log('User already authenticated and loaded, skipping validation')
                            return
                        }

                        dispatch(setUserDataLoaded(false))

                        const idToken = await user.getIdTokenResult()
                        const validateLink = process.env.REACT_APP_VALIDATE_EMAIL || ''

                        const customClaims = idToken.claims

                        const { isDriver, driverStatus, validAccess } = customClaims
                        
                        console.log('Auth validation starting...', { 
                            email: user.email, 
                            hasValidAccess: validAccess === 'VALID_EMAIL',
                            hasAuthenticatedInSession 
                        })
                        
                        // TBD: Do we need any other statuses?
                        if (isDriver && driverStatus === 'pending') {
                            await signOut(auth)
                            dispatch(resetUserData())
                            openSnackbar(`Your account is under review`)
                            return
                        }
                        
                        /*
                        {
                            role,
                            validAccess,
                            driverProfileDetails,
                            clients,
                        }
                        */
                        // Get Turnstile token from sessionStorage
                        const turnstileToken = sessionStorage.getItem('turnstileToken')
                        
                        // For already authenticated users on route changes, we can skip full validation
                        // The backend will verify the token's custom claims instead
                        
                        let validationResult
                        try {
                            isAuthenticatingRef.current = true
                            validationResult = await axios({
                                url: validateLink,
                                method: 'POST',
                                data: {
                                    email: user.email,
                                    token: idToken.token,
                                    customerId: process.env.REACT_APP_CLIENT,
                                    component: 'PORTAL',
                                    turnstileToken: turnstileToken || '',
                                },
                            })
                        } catch (error: any) {
                            // Handle authentication errors from backend
                            isAuthenticatingRef.current = false
                            sessionStorage.removeItem('turnstileToken')
                            
                            if (error?.response?.status === 403) {
                                const errorData = error?.response?.data
                                const errorType = errorData?.error || errorData?.validAccess
                                
                                // Check if user is pending approval or missing role
                                if (errorType === 'PENDING_APPROVAL' || errorType === 'NO_ROLE_ASSIGNED') {
                                    openSnackbar(errorData?.message || 'Your account is pending administrator approval')
                                    history.push('/pendingApproval')
                                    return
                                } else if (errorType === 'ACCOUNT_INACTIVE') {
                                    openSnackbar(errorData?.message || 'Your account has been deactivated')
                                    await signOut(auth)
                                    dispatch(resetUserData())
                                    setHasAuthenticatedInSession(false)
                                    history.push('/login')
                                    return
                                }
                            }
                            
                            // For other errors, sign out and redirect to login
                            openSnackbar(error?.response?.data?.message || 'Authentication failed. Please try again.')
                            await signOut(auth)
                            dispatch(resetUserData())
                            setHasAuthenticatedInSession(false)
                            history.push('/login')
                            return
                        } finally {
                            isAuthenticatingRef.current = false
                        }
                        
                        // Clear Turnstile token after use (tokens are single-use)
                        sessionStorage.removeItem('turnstileToken')
                        
                        // Mark that user has been authenticated in this session
                        setHasAuthenticatedInSession(true)
                        console.log('Auth validation successful, user authenticated')
                        
                        /* Validate response */
                        if (!validationResult || !validationResult.data) {
                            await signOut(auth)
                            dispatch(resetUserData())
                            openSnackbar('Invalid authentication response')
                            history.push('/login')
                            return
                        }
                        // Check validation result status
                        if (
                            validationResult.data &&
                            validationResult.data.validAccess === 'VALID_EMAIL' &&
                            validationResult.data.active &&
                            validationResult.data.role &&
                            validationResult.data.role.id
                        ) {
                            const userPayload = {
                                email: user.email,
                                firstName: validationResult.data.firstName || '',
                                lastName: validationResult.data.lastName || '',
                                phone: validationResult.data.phone || '',
                                isDriver: validationResult.data.isDriver || false,
                                isSBCustomer: validationResult.data.isSBCustomer || false,
                                isVendor: validationResult.data.isVendor || false,
                                isCarrier: validationResult.data.isCarrier || false,
                                carrier: validationResult.data.carrier || null,
                                role: validationResult.data.role,
                                isAllDistributionZones: validationResult.data.isAllDistributionZones || false,
                                userDistributionZoneIds: validationResult.data.distributionZones || [],
                                isAllClients: validationResult.data.isAllClients || false,
                                userClientIds: validationResult.data.clients || [],
                                id: validationResult.data.id,
                                completedGuidesWalkthrough: validationResult.data.completedGuidesWalkthrough || false,
                                systemSetupHasShown: validationResult.data.systemSetupHasShown || false,
                                // Per-mode pinned default views ({ [mode]: viewId }); avoids a
                                // separate USERS-doc read on the shipments page.
                                defaultViews: validationResult.data.defaultViews || {},
                            }
                            
                            /* global context , to later be replaced entirely by redux */
                            updateUser(user)

                            /* redux update */
                            dispatch(initUserData(userPayload))
                            clearUserRoleCache()
                            
                            /* capturing user actions - with null check for role */
                            if (userPayload?.role?.id) {
                                const docRef = await rolesCollection.getDocById(userPayload.role.id)
                                const roleDocData = docRef?.data() || {}
                                const actionsData = roleDocData.actions || {}
                                dispatch(updateActions(actionsData))
                                // Role-scoped column visible/editable state now lives on the
                                // ROLES_NEW doc (fieldVisibility/fieldEditability maps, keyed by
                                // field doc id per mode) so admin changes are captured in
                                // AdminHistory. Attach them to state.user.role so the OrderTable
                                // column build can read editability from the role.
                                dispatch(updateRole({
                                    ...userPayload.role,
                                    fieldVisibility: roleDocData.fieldVisibility || {},
                                    fieldEditability: roleDocData.fieldEditability || {},
                                }))
                                /* fetch authorized pre requisites based on user role */
                                dispatch(fetchUserData(userPayload.role.id))

                                if (!allPreFetchDataLoaded) {
                                    Promise.all([
                                        dispatch(fetchBillingData()),
                                        dispatch(fetchData({ roleId: userPayload.role.id, fieldVisibility: roleDocData.fieldVisibility || null })),
                                        dispatch(fetchDispatchData()),
                                    ])
                                }
                            }
                            
                            /* steps for the guide joy */
                            setTimeout(() => setRun(true), 1000)
                        } else if (!validationResult.data.active) {
                            // Account exists but is inactive
                            openSnackbar(`Your account has been deactivated. Please contact your administrator.`)
                            dispatch(resetUserData())
                            await signOut(auth)
                            history.push('/login')
                            return
                        } else if (!validationResult.data.role || !validationResult.data.role.id) {
                            // User exists but has no role assigned
                            openSnackbar(`No role has been assigned to your account. Please contact your administrator.`)
                            dispatch(resetUserData())
                            await signOut(auth)
                            history.push('/pendingApproval')
                            return
                        } else {
                            // Invalid account or other issues
                            openSnackbar(`Unable to access account. Please contact your administrator.`)
                            dispatch(resetUserData())
                            await signOut(auth)
                            history.push('/login')
                            return
                        }
                    } else {
                        await signOut(auth)
                        dispatch(resetUserData())
                        setHasAuthenticatedInSession(false)
                        isAuthenticatingRef.current = false
                        loadPublicModule(location.pathname || '/login')
                    }
                } catch (error) {
                    await signOut(auth)
                    dispatch(resetUserData())
                    setHasAuthenticatedInSession(false)
                    isAuthenticatingRef.current = false
                    loadPublicModule(location.pathname || '/login')
                }
            })
        } catch (error) {
            dispatch(resetUserData())
            setHasAuthenticatedInSession(false)
            isAuthenticatingRef.current = false
            return
        }

        return () => {
            clearTimeout(timeOut)
        }
    }, [])

    const checkIsPublic = useCallback((pathName: string) => {
        return (
            pathName.includes('forgotPassword') ||
            pathName.includes('sharedview') ||
            pathName.includes('signup') ||
            pathName.includes('login') ||
            pathName.includes('pendingApproval') ||
            pathName.includes('quickbooks-oauth-callback') ||
            // Customers reviewing an invoice have no portal account; the token in the
            // URL is resolved server-side by disputePublicApi.
            pathName.startsWith('/review/')
        )
    }, [])

    const loadPublicModule = useCallback((pathName: string) => {
        if (checkIsPublic(pathName)) {
            history.push(pathName)
        } else {
            history.push('/login')
        }
    }, [])

    /**
     * Initialize default params for shipments route (lastMile mode)
     * Sets all distribution zones, all vendors, and date range Oct 1 2025 - Dec 31 2028
     * Reuses existing OrderTable query logic - no new queries needed
     * Only runs once when route first loads to prevent query loops
     */
    useEffect(() => {
        // Reset initialization flag when leaving shipments route
        if (location.pathname !== '/shipments') {
            shipmentsInitializedRef.current = false
            return
        }

        // Only initialize once when route loads and data is ready
        if (
            location.pathname === '/shipments' && 
            userDataLoaded && 
            DISTRIBUTIONZONES && 
            VENDORS && 
            DISTRIBUTIONZONES.length > 0 && 
            VENDORS.length > 0 &&
            !shipmentsInitializedRef.current
        ) {
            shipmentsInitializedRef.current = true

            // If the user navigates away (or this effect re-runs) before the
            // async saved-view query resolves, this flag stops the stale result
            // from calling setSelectedView() on a grid that is unmounting —
            // which triggers the AG Grid "getGui() is null" race.
            let cancelled = false

            // Async function to initialize shipments with default view
            const initializeShipments = async () => {
                // Set date range: Oct 1 2025 to Dec 31 2028
                const fromDateAll = moment('2025-10-01').toDate()
                const toDateAll = moment('2028-12-31').toDate()

                // Map all distribution zones (same format as DateRegion)
                const allRegionsSpread = DISTRIBUTIONZONES.map((region: any) => ({
                    label: region?.label,
                    id: region?.value?.id,
                    DistributionZoneId: region?.value?.id,
                    TimeZone: region?.value?.TimeZone,
                    value: region?.value,
                }))

                // Map all vendors (same format as DateRegion)
                const allVendorsSpread = VENDORS.map((vendor: any) => ({
                    label: vendor?.label,
                    id: vendor?.value?.id,
                    value: vendor?.value,
                }))

                // Use lastMile mode and StartWindow date field
                const defaultMode = 'lastMile'
                const defaultDateField = { label: 'Start Window', value: 'StartWindow' }

                // Only mark "all" when the user actually has access to every
                // client/zone. allRegionsSpread/allVendorsSpread are already scoped to
                // the user's permitted set, but setting the all-flags true makes the
                // query builder drop the per-client/zone filter and fetch the whole
                // zone — leaking other clients' orders to a restricted user. Keep them
                // false for restricted users so the selected lists drive a real filter.
                const hasAllClientAccess = role?.name === 'SUPER_ADMIN' || !!isAllClients
                const hasAllZoneAccess = role?.name === 'SUPER_ADMIN' || !!isAllDistributionZones

                selectTableSelectedDate({
                    from: fromDateAll,
                    to: toDateAll,
                    mode: defaultMode,
                    dateField: defaultDateField,
                    selectedRegions: allRegionsSpread || [],
                    selectedVendors: allVendorsSpread || [],
                    isAllVendorSelected: hasAllClientAccess,
                    isAllRegionSelected: hasAllZoneAccess,
                    selectedCategories: [],
                    selectedManufacturers: [],
                    selectedStatuses: [],
                })

                // Save to localStorage (same as DateRegion does)
                localStorage.setItem(
                    'selectedDate',
                    JSON.stringify({
                        from: fromDateAll,
                        to: toDateAll,
                        mode: defaultMode,
                        selectedRegions: allRegionsSpread || [],
                        selectedVendors: allVendorsSpread || [],
                        isAllVendorSelected: hasAllClientAccess,
                        isAllRegionSelected: hasAllZoneAccess,
                        dateField: defaultDateField,
                        selectedCategories: [],
                        selectedManufacturers: [],
                        selectedStatuses: [],
                    })
                )

                updateUIMode(defaultMode)
                
                // Try to load "DAILY VIEW NEW" view, fallback to default if not found
                try {
                    const dailyViewResult = await tableViewsCollection.getByQueries([
                        { fieldPath: 'name', opStr: '==', value: 'DAILY VIEW NEW' },
                        { fieldPath: 'mode', opStr: '==', value: defaultMode },
                        { fieldPath: 'type', opStr: '==', value: 'shared' },
                    ])

                    if (cancelled) return

                    if (dailyViewResult?.docs && dailyViewResult.docs.length > 0) {
                        const viewData = dailyViewResult.docs[0].data()
                        if (viewData && typeof viewData === 'object') {
                            const dailyView = {
                                ...(viewData as any),
                                id: dailyViewResult.docs[0].id,
                            }
                            setSelectedView(dailyView)
                        } else {
                            // Invalid view data, use default ag-grid view
                            setSelectedView(null)
                        }
                    } else {
                        // View not found, use default ag-grid view
                        setSelectedView(null)
                    }
                } catch (error) {
                    console.error('Error loading DAILY VIEW NEW:', error)
                    if (cancelled) return
                    // On error, use default ag-grid view
                    setSelectedView(null)
                }

                addCountForFetch()
            }

            initializeShipments()

            return () => {
                cancelled = true
            }
        }
    }, [location.pathname, userDataLoaded, DISTRIBUTIONZONES?.length, VENDORS?.length, setSelectedView])

    const loadModule = useCallback(
        (rawPathName: string) => {
            // Every case below is an exact string match, so a trailing slash silently
            // falls through to the default branch and renders the wrong page. Links
            // arriving from outside the app (email clients in particular) often carry
            // one, so normalise before matching. '/' itself must survive.
            const pathName = rawPathName.length > 1 ? rawPathName.replace(/\/+$/, '') : rawPathName
            console.log('[loadModule] Called with pathName:', pathName)
            // Check full path first (e.g., /admin/fields), then fallback to first segment (e.g., /admin)
            const path = `/${pathName.split('/')[1]}`
            const hasFullPathAccess = checkHasAccessModule(pathName)
            const hasParentPathAccess = checkHasAccessModule(path)
            console.log('[loadModule] Access check:', { pathName, path, hasFullPathAccess, hasParentPathAccess })

            // If user doesn't have access to either the full path or parent path, redirect
            if (!hasFullPathAccess && !hasParentPathAccess) {
                console.log('Access denied to:', pathName)
                const hasRecordAccess = checkHasAccessModule('/dateRegion')

                hasRecordAccess
                    ? pathName === '/'
                        ? history.push('/shipments')
                        : history.goBack()
                    : history.push('/trackOrder')
                return
            }
            
            // For nested admin routes, check specific permissions
            if (pathName.startsWith('/admin/') && pathName !== '/admin') {
                console.log('[mainRoutes] Checking nested admin route:', pathName, 'hasFullPathAccess:', hasFullPathAccess)
                if (!hasFullPathAccess) {
                    console.log('Access denied to nested admin route:', pathName)
                    openSnackbar('You do not have permission to access this page')
                    history.goBack()
                    return
                }
            }

            console.log('[mainRoutes] loadModule switch for pathName:', pathName)
            switch (pathName) {
                //Public Routes
                case '/sharedview':
                case `/sharedview/${viewId}`:
                    return <SharedView isSharedView={true} sharedViewId={viewId} />
                case '/signup':
                    return <SignUp />
                case '/login':
                    return <Login />
                case '/pendingApproval':
                    return <PendingApproval />
                case '/forgotPassword':
                    return <ForgotPassword />
                case '/changePassword':
                    return <ChangePassword />
                case '/quickbooks-oauth-callback':
                    return <QuickBooksOAuthCallback />
                case `/review/${tokenId}`:
                    return <DisputeReview tokenId={tokenId} />

                //Private Routes
                case '/':
                case '/dashboard':
                    return <Dashboard />
                case '/mainContainer':
                case '/dateRegion':
                    return <DateRegion />
                case '/analytics':
                    return <Reports />
                case '/kpis':
                    return <KPIs />
                case '/records':
                case '/orderTable':
                case '/shipments':
                    return (
                        <RouteErrorBoundary name="OrderTable">
                            <OrderTable />
                        </RouteErrorBoundary>
                    )
                case '/email-intake':
                    return (
                        <RouteErrorBoundary name="EmailIntake">
                            <EmailIntake />
                        </RouteErrorBoundary>
                    )
                case '/mapFleet':
                case '/mapFleet/network':
                case '/mapFleet/routing':
                    return <MapFleet />
                case '/search':
                    return <Search closeSearchModal={() => {
                        // Navigate back to dateRegion if user came from there, otherwise go to dashboard
                        const fromRoute = (location.state as { from?: string })?.from
                        if (fromRoute === '/dateRegion') {
                            history.push('/dateRegion')
                        } else {
                            history.push('/dashboard')
                        }
                    }} />
                case '/dispatch':
                    return <Dispatch />
                case '/disputes':
                    return <Disputes />
                case '/liveConnect':
                    return <LiveConnect />
                case '/reports':
                    return <Reports />
                case '/admin':
                    return <Admin />
                case '/assets':
                    return <Assets />
                case '/admin/role':
                    return <AdminRoleSettings />
                case '/admin/userRole':
                    return <AdminUserRoleSettings />
                case '/admin/vendor':
                    return <VenderProfileSettings />
                case '/admin/contractor':
                    return <ContractorProfileSettings />
                case '/admin/notifications':
                    return <NotificationSettings />
                case '/admin/automations':
                    return <AutomationSettings />
                case '/admin/services':
                    console.log('[mainRoutes] Rendering ServiceTypeSettings for /admin/services')
                    return <ServiceTypeSettings />
                case '/admin/order-templates':
                    return <AdminOrderTemplatesSettings />
                case '/admin/calendarSettings':
                    return <CalendarSettings />
                case '/admin/fields':
                    return <FieldManagement />
                case '/admin/email-templates':
                    return <EmailTemplates />
                case '/admin/addressBook':
                    return <AddressBookSettings />
                case '/admin/billing/substatus':
                    return <SubStatus />
                case `/admin/billing/ratetable`:
                    return <RateTableSettings id={''} />
                case `/admin/billing/ratetable/${rateId}`:
                    return <RateTableSettings id={rateId} />
                case '/admin/billing/taxes':
                    return <Taxes />
                case '/admin/billing/fscrates':
                    return <FSCRates />
                case '/admin/billing/weeklyfscrates':
                    return <WeeklyFSCRates />
                case '/admin/billing/services':
                    return <Services />
                case '/admin/billing/conditions':
                    return <Conditions />
                case '/admin/billing/tiersEscrow':
                    return <TiersEscrow />
                case '/admin/billing/globalUnits':
                    return <GlobalUnits />
                case '/admin/billing/fsc':
                    return <FSC />
                case '/admin/rates':
                    return <RatesManagement />
                case '/admin/rates/partner-rate-tables':
                    return <RateTablesManagement />
                case '/admin/integrations/external':
                    return <ExternalIntegrations />
                case '/admin/integrations/internal':
                    return <InternalIntegrations />
                case '/admin/integrations/quickbooks':
                    return <QuickBooksIntegration />
                case '/admin/ai/agent-configuration':
                    return <AgentConfiguration />
                case '/admin/ai/shyftbase':
                    return <ShyftbaseAI />
                case '/admin/ai/optimizer':
                    return <Optimizer />
                // Mid-Mile Admin Routes (Legacy - keeping for backward compatibility)
                case '/admin/midmile':
                    return <MidMileAdmin />
                case '/admin/midmile/rate-tables':
                    return <RateTablesManagement />
                case '/admin/midmile/tariffs':
                    return <TariffBuilder />
                case '/admin/midmile/bidding':
                    return <BiddingConfiguration />
                case '/admin/midmile/settings':
                    return <MidMileSettings />
                case '/midmile/shipments/create':
                    return <MidMileShipmentCreate />
                case '/admin/midmile/bidding/active':
                    return <ActiveBidding />
                
                // Transport Partners Management Routes
                case '/admin/transport-partners/directory':
                    return <PartnerDirectory />
                case '/admin/transport-partners/profiles':
                    return <PartnerProfiles />
                case '/admin/transport-partners/drivers':
                    return <Drivers />
                case '/admin/transport-partners/rate-intake':
                    return <AIRateIntake />
                
                case '/admin/billing/audit':
                    return <AuditAdjustments />
                
                // Operations & Routing Routes (Phase 1 - Priority 1)
                case '/admin/operations/capacity-management':
                    return <CapacityManagement />
                case '/admin/operations/re-optimization':
                    return <ReOptimization />
                
                // Compliance Routes (Phase 1 - Priority 1)
                case '/admin/compliance/gdpr':
                    return <GDPR />
                case '/admin/compliance/country-rules':
                    return <CountryRules />
                case '/admin/compliance/audit-trails':
                    return <AuditTrails />
                
                default:
                    return <DateRegion />
            }
        },
        [rateId, viewId, tokenId, history, openSnackbar]
    )

    return (
        <div className={styles.app}>
            {!role && checkIsPublic(location.pathname) ? (
                <div className={'shyftbase_main_layout_public'}>
                    <div className="shyftbase_main_layout_content">
                        <div style={{ overflow: 'hidden' }}>
                            <>
                                <Suspense fallback={<LoadingScreen />}>{loadModule(location.pathname || '/')}</Suspense>
                            </>
                        </div>
                    </div>
                </div>
            ) : !(allPreFetchDataLoaded && userDataLoaded) ? (
                <LoadingScreen />
            ) : (
                <div className={'shyftbase_main_layout'}>
                    <div>{<Nav />}</div>
                    <div className="shyftbase_main_layout_content">
                        <div style={{ overflow: 'hidden' }}>
                            <>
                                <Suspense fallback={<LoadingScreen />}>{loadModule(location.pathname || '/')}</Suspense>
                            </>
                        </div>
                    </div>
                </div>
            )}
            {user && !completedGuidesWalkthrough && false &&(
                <Joyride
                    continuous
                    disableOverlayClose
                    run={run}
                    steps={steps}
                    stepIndex={guidesStepIndex} // Control the current step with the index from context
                    callback={handleJoyrideCallback}
                    scrollToFirstStep={true}
                    showProgress={true}
                    showSkipButton={true}
                    styles={{
                        options: {
                            width: 500,
                            zIndex: 1000,
                            primaryColor: '#000',
                        },
                    }}
                />
            )}
            {user && <AIAssistant />}
        </div>
    )
}
export default React.memo(MainRoutes)
